import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar, Search, FileText, Clock, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase, getCurrentUser } from "@/lib/supabase";

const BuyerDashboard = () => {
  const navigate = useNavigate();
  
  // State
  const [buyer, setBuyer] = useState<any>(null);
  const [rfqs, setRfqs] = useState<any[]>([]);
  const [quotesReceived, setQuotesReceived] = useState<any[]>([]);
  const [recentSearches, setRecentSearches] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Load buyer dashboard data
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const user = await getCurrentUser();
      if (!user) {
        console.error('No authenticated user found');
        setLoading(false);
        return;
      }

      // Get buyer profile
      const { data: profileData, error: profileError } = await supabase
        .from('accounts')
        .select('user_type, full_name, id, email, phone, avatar_url, company_name, job_title, city, state, is_verified')
        .eq('id', user.id);
        
      if (profileError) {
        console.error('Error fetching buyer profile:', profileError);
        setLoading(false);
        return;
      }

      // Handle case where no profile exists
      if (!profileData || profileData.length === 0) {
        navigate('/account-setup');
        return;
      }

      const profile = profileData[0];
      
      // Check if user is a buyer
      if (profile.user_type !== 'buyer' && profile.user_type !== 'both') {
        console.error('User is not a buyer');
        setLoading(false);
        return;
      }
      
      // Format buyer data
      const buyerData = {
        id: profile.id || '',
        name: profile.full_name || 'Buyer',
        email: profile.email || '',
        phone: profile.phone || '',
        avatar: profile.avatar_url || 'https://github.com/polymet-ai.png',
        company: profile.company_name || '',
        position: profile.job_title || 'Buyer',
        location: `${profile.city || ''}, ${profile.state || ''}`,
        notificationCount: 0,
        verificationStatus: profile.is_verified ? 'verified' : 'pending'
      };
      
      setBuyer(buyerData);
      
      try {
        // Get RFQs created by the buyer
        const { data: rfqData, error: rfqError } = await supabase
          .from('rfq_requests')
          .select(`
            id,
            title,
            description,
            status,
            quantity,
            budget_min,
            budget_max,
            currency,
            created_at,
            expiry_date
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        if (rfqError) {
          console.error('Error fetching RFQs:', rfqError);
        } else {
          setRfqs(rfqData || []);
        }
      } catch (error) {
        console.error('Error processing RFQs:', error);
      }
      
      try {
        // Get quotes/responses received for buyer's RFQs
        const { data: quotesData, error: quotesError } = await supabase
          .from('rfq_responses')
          .select(`
            id,
            rfq_id,
            supplier_id,
            quoted_price,
            currency,
            delivery_time,
            notes,
            status,
            created_at,
            rfq_requests!inner(title, user_id),
            supplier:profiles!rfq_responses_supplier_id_fkey(full_name)
          `)
          .eq('rfq_requests.user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);

        // Fallback to simple query if complex query fails
        if (quotesError && quotesError.code === '42P01') {
          // Get buyer's RFQ IDs first
          const { data: buyerRfqs } = await supabase
            .from('rfq_requests')
            .select('id')
            .eq('user_id', user.id);

          if (buyerRfqs && buyerRfqs.length > 0) {
            const rfqIds = buyerRfqs.map(rfq => rfq.id);
            
            const { data: quotesDataSimple, error: quotesErrorSimple } = await supabase
              .from('rfq_responses')
              .select('*')
              .in('rfq_id', rfqIds)
              .order('created_at', { ascending: false });
            
            if (!quotesErrorSimple && quotesDataSimple) {
              // Manually fetch RFQ titles and supplier names
              for (let quote of quotesDataSimple) {
                // Get RFQ title
                const { data: rfqData } = await supabase
                  .from('rfq_requests')
                  .select('title')
                  .eq('id', quote.rfq_id)
                  .single();
                
                quote.rfq_title = rfqData?.title || 'Unknown RFQ';
                
                // Get supplier name
                const { data: supplierData } = await supabase
                  .from('profiles')
                  .select('full_name')
                  .eq('id', quote.supplier_id)
                  .single();
                
                quote.supplier_name = supplierData?.full_name || 'Unknown Supplier';
              }
              setQuotesReceived(quotesDataSimple);
            }
          }
        } else if (quotesError) {
          console.error('Error fetching quotes:', quotesError);
        } else {
          setQuotesReceived(quotesData || []);
        }
      } catch (error) {
        console.error('Error processing quotes:', error);
      }
      
      try {
        // Get recent searches - handle missing table gracefully
        const { data: searchesData, error: searchesError } = await supabase
          .from('search_history')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (searchesError) {
          if (searchesError.code === '42P01') {
            setRecentSearches([]);
          } else {
            console.error('Error fetching recent searches:', searchesError);
            setRecentSearches([]);
          }
        } else {
          setRecentSearches(searchesData || []);
        }
      } catch (error) {
        console.error('Error processing recent searches:', error);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  // Handle create RFQ navigation
  const handleCreateRFQ = () => {
    navigate('/rfq/create');
  };

  // Get status color for RFQ
  const getRfqStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'outline';
      case 'published': return 'default';
      case 'closed': return 'secondary';
      case 'fulfilled': return 'secondary';
      default: return 'outline';
    }
  };

  // Get status color for quotes
  const getQuoteStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'outline';
      case 'accepted': return 'default';
      case 'rejected': return 'destructive';
      case 'negotiating': return 'secondary';
      default: return 'outline';
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2 border-primary">
            <AvatarImage src={buyer?.avatar} />
            <AvatarFallback>{buyer?.name?.charAt(0) || 'B'}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{buyer?.name || 'Buyer Dashboard'}</h1>
            <p className="text-muted-foreground">{buyer?.company || buyer?.position || 'Industrial Buyer'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/account')}
          >
            Edit Profile
          </Button>
          
          <Button onClick={handleCreateRFQ}>Create RFQ</Button>
        </div>
      </div>

      {/* Dashboard Content */}
      <Tabs defaultValue="rfqs" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6">
          <TabsTrigger value="rfqs">My RFQs</TabsTrigger>
          <TabsTrigger value="quotes">Quotes Received</TabsTrigger>
          <TabsTrigger value="searches">Recent Searches</TabsTrigger>
        </TabsList>

        {/* RFQs tab */}
        <TabsContent value="rfqs">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Your RFQs ({rfqs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rfqs.length > 0 ? (
                <div className="space-y-2">
                  {rfqs.slice(0, 5).map((rfq) => (
                    <div key={rfq.id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-center">
                        <p className="font-medium">{rfq.title}</p>
                        <Badge variant={getRfqStatusColor(rfq.status)}>
                          {rfq.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {rfq.description}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Qty: {rfq.quantity}</span>
                          {rfq.budget_min && rfq.budget_max && (
                            <span>Budget: {rfq.currency} {rfq.budget_min} - {rfq.budget_max}</span>
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/buyer-dashboard/rfqs`)}
                        >
                          View Details
                        </Button>
                      </div>
                      {rfq.expiry_date && (
                        <div className="flex items-center text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3 mr-1" />
                          Expires: {new Date(rfq.expiry_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                  {rfqs.length > 5 && (
                    <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/buyer-dashboard/rfqs')}>
                      View All RFQs
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">No RFQs created yet.</p>
                  <p className="text-sm text-muted-foreground mb-4">Create your first quote request to get started!</p>
                  <Button onClick={handleCreateRFQ}>Create First RFQ</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Quotes Received tab */}
        <TabsContent value="quotes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Quotes Received ({quotesReceived.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {quotesReceived.length > 0 ? (
                <div className="space-y-2">
                  {quotesReceived.slice(0, 5).map((quote) => (
                    <div key={quote.id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-center">
                        <p className="font-medium">{quote.rfq_title || quote.rfq_requests?.title || 'Quote Response'}</p>
                        <Badge variant={getQuoteStatusColor(quote.status || 'pending')}>
                          {quote.status || 'pending'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-sm text-muted-foreground">
                          From: {quote.supplier_name || quote.supplier?.full_name || 'Supplier'}
                        </p>
                        <p className="font-medium text-green-600">
                          {quote.currency || '₹'} {quote.quoted_price}
                        </p>
                      </div>
                      {quote.delivery_time && (
                        <div className="flex items-center text-xs text-muted-foreground mt-1">
                          <Calendar className="h-3 w-3 mr-1" />
                          Delivery: {quote.delivery_time}
                        </div>
                      )}
                      {quote.notes && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {quote.notes}
                        </p>
                      )}
                      <div className="flex justify-end mt-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/buyer-dashboard/rfqs`)}
                        >
                          View Quote
                        </Button>
                      </div>
                    </div>
                  ))}
                  {quotesReceived.length > 5 && (
                    <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/buyer-dashboard/rfqs')}>
                      View All Quotes
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">No quotes received yet.</p>
                  <p className="text-sm text-muted-foreground mb-4">Create RFQs to start receiving quotes from suppliers!</p>
                  <Button variant="outline" onClick={handleCreateRFQ}>Create RFQ</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Recent Searches tab */}
        <TabsContent value="searches">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Recent Searches ({recentSearches.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentSearches.length > 0 ? (
                <div className="space-y-2">
                  {recentSearches.map((search) => (
                    <div key={search.id} className="p-3 border rounded-lg flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <p>{search.query}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/search?q=${encodeURIComponent(search.query)}`)}
                      >
                        Search Again
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">No recent searches.</p>
                  <p className="text-sm text-muted-foreground mb-4">Start searching for products to see your history here!</p>
                  <Button variant="outline" onClick={() => navigate('/')}>
                    Start Searching
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BuyerDashboard;
